/**
 * Remembers which API the parent last logged into, per browser.
 *
 * The web counterpart of Android's `BackendConfigStore`: there, a
 * DataStore preference overrides `BuildConfig.BASE_URL`; here,
 * `localStorage` overrides the deployment default the BFF reports from
 * `BACKEND_BASE_URL`. Same purpose — point the client at a different
 * host without rebuilding or restarting anything — and, like the Android
 * one, deliberately *not* one of the secure stores: this is a hostname,
 * not a secret. The credentials it is used with never touch the browser's
 * storage (see `api/client.ts`).
 *
 * Every access is wrapped: `localStorage` throws outright in some
 * privacy modes, and an unreadable preference must degrade to "use the
 * deployment default", never to a broken login screen.
 */

const STORAGE_KEY = 'pc_backend_base_url';

export function readStoredBackendBaseUrl(): string | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === null || stored.trim() === '' ? null : stored;
  } catch {
    return null;
  }
}

export function storeBackendBaseUrl(url: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, url);
  } catch {
    // Not persisting is a degraded experience (the field falls back to
    // the deployment default next visit), not a failure worth surfacing.
  }
}

export function clearStoredBackendBaseUrl(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // See above.
  }
}
